import { getAccessToken, resetAccessToken } from '@/helpers/utils';
import { local } from '@/local';
import Vue from '@/main';
import router from '@/routes';
import axios from 'axios';
import { encryptUrlPlaceholders } from '@/helpers/encryption';

const apiBaseURL = process.env.BASE_URL + '/lawsuit/';
axios.defaults.baseURL = apiBaseURL;
axios.defaults.headers.common.Authorization = getAccessToken();

const skippedFromLoaderUrls = ['/system/api/nafath/get-otp', '/system/api/nafath/check-status'];

const getResponseErrorMessages = (error) => {
  if (!error.response || !error.response.status) {
    error.isHandled = false;
    if (error.message) return local.errors.somethingWentWrong;
    return false;
  }
  const errorData = error.response && error.response.data;
  if (!errorData) {
    error.isHandled = false;
    return local.errors.somethingWentWrong;
  }
  switch (error.response.status) {
    case 401:
    case 403: {
      return '';
    }
    case 409:
    case 422:
    case 400: {
      error.isHandled = true;
      if (typeof errorData === 'object') {
        if (errorData.detail) return errorData.detail;
        if (typeof errorData.errors === 'object')
          return Object.keys(errorData.errors)
            .reduce((errors, errorKey) => {
              const msg = errorData.errors[errorKey];
              if (Array.isArray(msg)) errors += `- ${msg} <br>`;
              else errors += `- ${msg}`;
              return errors;
            }, ``);
        error.isHandled = false;
        return local.errors.somethingWentWrong;
      } else {
        return errorData;
      }
    }
    default: {
      error.isHandled = false;
      return local.errors.somethingWentWrong;
    }
  }
};
const methodsToHaveOverlayLoader = ['post', 'patch', 'put'];

axios.interceptors.request.use(
  (config) => {
    // do something before request is
    if (skippedFromLoaderUrls.includes(config.url))
      return config;

    Vue.appLoader.start();
    try { removeShownProperty(config.data); } catch (error) { }
    if (methodsToHaveOverlayLoader.indexOf(config.method) !== -1) {
      Vue.overlayLoader.start();
    }

    if (config.url && config.url.includes('{encrypt:')) {
      // encrypt and replace placeholders using values from config.encryptParams
      config.url = encryptUrlPlaceholders(config.url, config.encryptParams || {});
    }

    return config;
  },
  (error) => {
    // do something with request error
    Vue.appLoader.error();
    Vue.overlayLoader.end();
    Vue.toaster.error(local.errors.somethingWentWrong);
    return Promise.reject(error);
  }
);

// add a response interceptor
axios.interceptors.response.use(
  (response) => {
    // do something with response data
    Vue.appLoader.end();
    Vue.overlayLoader.end();
    return response;
  },
  (error) => {
    Vue.appLoader.error();
    Vue.overlayLoader.end();
    const status = error.response && error.response.status;
    if (status === 401) {
      resetAccessToken();
      location.reload();
    }
    if (status === 403) {
      router.push('/error/403');
    }
    if (status === 404 && error.response.config.method === 'get') return Promise.reject(error);
    if (status === 404 && error.response.config.method === 'head') return Promise.reject(error);

    // do something with response error
    const errorMessage = getResponseErrorMessages(error);
    if (errorMessage) {
      !error.config.overrideErrorHandlingBehaviour && Vue.toaster.error(errorMessage);
      error.message = errorMessage;
      error.handled = error.isHandled;
    }
    return Promise.reject(error);
  }
);

export const getRequestFullURL = (url, cancelCallback = () => { }) =>
  axios.get(url, { cancelToken: new axios.CancelToken(cancelCallback) });

export const postRequestFullURL = (url, body, cancelCallback = () => { }) =>
  axios.post(url, body, { cancelToken: new axios.CancelToken(cancelCallback) });

export const getRequest = (url, cancelCallback = () => { }) =>
  getRequestFullURL(`${apiBaseURL}${url}`, cancelCallback);

export const postRequest = (url, body, cancelCallback = () => { }) =>
  postRequestFullURL(`${apiBaseURL}${url}`, body, cancelCallback);

export const deleteRequest = (url, body, cancelCallback = () => { }) =>
  axios.delete(`${apiBaseURL}${url}`, body, { cancelToken: new axios.CancelToken(cancelCallback) });

const removeShownProperty = (obj) => {
  if (typeof obj === 'object' && Array.isArray(obj) && !!obj) (obj || []).forEach((arrElem) => { removeShownProperty(arrElem); });
  if (typeof obj === 'object' && !Array.isArray(obj) && !!obj)
    for (const key in (obj || {})) {
      if (Object.hasOwnProperty.call(obj, key)) {
        if (key === 'shown') delete obj[key];
        else removeShownProperty(obj[key]);
      }
    }
};
